import backgroundImage1 from 'assets/images/meetings/backgrounds/background1.jpg'
import backgroundImage2 from 'assets/images/meetings/backgrounds/background2.jpg'
import backgroundImage3 from 'assets/images/meetings/backgrounds/background3.jpg'
import backgroundImage4 from 'assets/images/meetings/backgrounds/background4.jpg'
import { Track } from 'livekit-client'
import type { LocalParticipant, LocalVideoTrack } from 'livekit-client'

/**
 * Type guard to check if a track is a LocalVideoTrack
 */
function isLocalVideoTrack(track: Track): track is LocalVideoTrack {
  return track && track.kind === Track.Kind.Video && track.source === Track.Source.Camera
}

/**
 * Checks if virtual background is supported in the current browser
 */
export async function isVirtualBackgroundSupported(): Promise<boolean> {
  try {
    // Check if the module exists and can be imported
    const module = await import('@livekit/track-processors')
    if (!module.supportsBackgroundProcessors) {
      return false
    }
    return module.supportsBackgroundProcessors()
  } catch (error) {
    // Module not installed or not available
    return false
  }
}

/**
 * Applies virtual background to a specific video track (for prejoin use)
 */
export async function applyVirtualBackgroundToTrack(
  track: LocalVideoTrack,
  backgroundType: string
): Promise<boolean> {
  try {
    if (track.source !== Track.Source.Camera) {
      return false
    }

    // Check if browser supports virtual backgrounds (except for disabled)
    if (backgroundType !== 'disabled') {
      const isSupported = await isVirtualBackgroundSupported()
      if (!isSupported) {
        return false
      }
    }

    // Special case for disabled: just remove existing processor
    if (backgroundType === 'disabled') {
      if (track.stopProcessor) {
        await track.stopProcessor()
      }
      return true
    }

    // Prepare the new processor before removing the old one to minimize visible gap
    let newProcessor
    try {
      const module = await import('@livekit/track-processors')

      switch (backgroundType) {
        case 'blur':
          if (!module.BackgroundBlur) return false
          newProcessor = module.BackgroundBlur(15)
          break
        case 'background1':
          if (!module.VirtualBackground) return false
          newProcessor = module.VirtualBackground(backgroundImage1)
          break
        case 'background2':
          if (!module.VirtualBackground) return false
          newProcessor = module.VirtualBackground(backgroundImage2)
          break
        case 'background3':
          if (!module.VirtualBackground) return false
          newProcessor = module.VirtualBackground(backgroundImage3)
          break
        case 'background4':
          if (!module.VirtualBackground) return false
          newProcessor = module.VirtualBackground(backgroundImage4)
          break
        default:
          return false
      }

      // Apply the new processor directly (this will replace the existing one)
      await track.setProcessor(newProcessor)
      return true
    } catch (error) {
      return false
    }
  } catch (error) {
    return false
  }
}

/**
 * Applies virtual background to the local participant's camera track based on type
 */
export async function applyVirtualBackground(
  localParticipant: LocalParticipant,
  backgroundType: string
): Promise<boolean> {
  try {
    // Check if browser supports virtual backgrounds
    const isSupported = await isVirtualBackgroundSupported()
    if (!isSupported && backgroundType !== 'disabled') {
      return false
    }

    // Get the local video track
    const videoTrackPublication = Array.from(localParticipant.videoTrackPublications.values())[0]
    const videoTrack = videoTrackPublication?.track

    if (!videoTrack || !isLocalVideoTrack(videoTrack)) {
      return false
    }

    // Special case for disabled: just remove existing processor
    if (backgroundType === 'disabled') {
      if (videoTrack.stopProcessor) {
        await videoTrack.stopProcessor()
      }
      return true
    }

    // Prepare the new processor before removing the old one to minimize visible gap
    let newProcessor
    try {
      const module = await import('@livekit/track-processors')

      switch (backgroundType) {
        case 'blur':
          if (!module.BackgroundBlur) return false
          newProcessor = module.BackgroundBlur(15)
          break
        case 'background1':
          if (!module.VirtualBackground) return false
          newProcessor = module.VirtualBackground(backgroundImage1)
          break
        case 'background2':
          if (!module.VirtualBackground) return false
          newProcessor = module.VirtualBackground(backgroundImage2)
          break
        case 'background3':
          if (!module.VirtualBackground) return false
          newProcessor = module.VirtualBackground(backgroundImage3)
          break
        case 'background4':
          if (!module.VirtualBackground) return false
          newProcessor = module.VirtualBackground(backgroundImage4)
          break
        default:
          return false
      }

      // Apply the new processor directly (this will replace the existing one)
      await videoTrack.setProcessor(newProcessor)
      return true
    } catch (error) {
      return false
    }
  } catch (error) {
    return false
  }
}
